import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Header overflow menu. Renders a "Mere ▾" trigger that opens a small
 * dropdown listing nav items the parent header has chosen to defer.
 *
 * Why this exists: with operator-role chrome the header had nine
 * top-level links and wrapped to two lines on common laptop widths.
 * Splitting the list into "primary" (kept inline) and "overflow"
 * (rendered here) keeps the chrome compact without dropping any
 * functionality.
 *
 * Behaviour:
 *  * Closes on outside click and on Escape, plus on route change
 *    (useLocation is in the dep array — React Router emits a new
 *    location object per navigation, so the effect re-fires).
 *  * Trigger highlights when any of its children matches the active
 *    route, so the user knows which "branch" they're in even when
 *    the active item is collapsed.
 */
export interface NavOverflowItem {
  to: string;
  label: string;
  /** Optional count rendered as an amber badge (e.g. offers awaiting you). */
  badge?: number;
}

interface NavOverflowProps {
  items: NavOverflowItem[];
  /** Optional override label — defaults to t('header.more'). */
  label?: string;
  /**
   * The single nav path the header has resolved as active (longest
   * match). When supplied, exactly one item can be active and the
   * trigger highlights only when that item lives in this menu. Without
   * it the menu falls back to plain prefix matching.
   */
  activeTo?: string | null;
}

export function NavOverflow({ items, label, activeTo }: NavOverflowProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  // Close on outside click + Escape.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  // Close on route change.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const isActive = (to: string): boolean =>
    activeTo !== undefined
      ? to === activeTo
      : location.pathname === to ||
        (to !== '/' && location.pathname.startsWith(to));
  const childActive = items.some((i) => isActive(i.to));
  const badgeTotal = items.reduce((sum, i) => sum + (i.badge ?? 0), 0);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`relative px-3 py-1.5 text-sm transition-colors flex items-center gap-1 ${
          childActive
            ? 'text-[var(--color-ink)] font-medium'
            : 'text-[var(--color-ink-soft)] hover:text-[var(--color-ink)]'
        }`}
      >
        {label ?? t('header.more')}
        {badgeTotal > 0 && (
          <span
            aria-label={t('header.offersBadge', { count: badgeTotal })}
            className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--color-alert)] text-white text-[10px] font-mono tabular"
          >
            {badgeTotal}
          </span>
        )}
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}
          strokeWidth={1.75}
        />
        {childActive && (
          <motion.span
            layoutId="nav-underline"
            className="absolute left-3 right-6 -bottom-px h-[2px] bg-[var(--color-primary)]"
            transition={{ type: 'tween', duration: 0.22, ease: [0.2, 0, 0, 1] }}
          />
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            role="menu"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.18, ease: [0.2, 0, 0, 1] }}
            className="absolute right-0 top-full mt-2 min-w-[220px] bg-white border border-[var(--color-rule)] shadow-[0_4px_20px_-8px_rgba(15,23,42,0.15)] z-50"
          >
            {items.map((item) => {
              const active = isActive(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  role="menuitem"
                  className={`flex items-center justify-between gap-3 px-4 py-2.5 text-sm border-b border-[var(--color-rule)] last:border-b-0 transition-colors ${
                    active
                      ? 'bg-[var(--color-paper-deep)] text-[var(--color-ink)] font-medium'
                      : 'text-[var(--color-ink-soft)] hover:bg-[var(--color-paper-deep)] hover:text-[var(--color-ink)]'
                  }`}
                >
                  {item.label}
                  {item.badge ? (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--color-alert)] text-white text-[10px] font-mono tabular">
                      {item.badge}
                    </span>
                  ) : null}
                </Link>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
